import { supabase } from '../lib/supabase';

export interface AnonymizationPreview {
  success: boolean;
  error?: string;
  player?: {
    id: number;
    name: string;
    prename: string;
    email: string;
  };
  games_affected: number;
  clubs_affected: number;
  recent_games: Array<{
    id: number;
    player_a: string;
    player_b: string;
    created_at: string;
  }>;
}

export interface AnonymizationResult {
  success: boolean;
  error?: string;
  anonymous_player_id?: number;
  games_affected?: number;
  clubs_affected?: number;
  original_name?: string;
}

export interface AdminAction {
  id: number;
  admin_name: string;
  action_type: string;
  target_player_name: string;
  details: any;
  created_at: string;
}

/**
 * Get preview of what will happen when anonymizing a player
 */
export const getAnonymizationPreview = async (playerId: number): Promise<AnonymizationPreview> => {
  const { data, error } = await supabase
    .rpc('get_anonymization_preview', { p_player_id: playerId });

  if (error) {
    console.error('Error getting anonymization preview:', error);
    throw error;
  }

  return data;
};

/**
 * Anonymize a player (hard delete with replacement)
 */
export const anonymizePlayer = async (playerId: number): Promise<AnonymizationResult> => {
  const { data, error } = await supabase
    .rpc('anonymize_player', { 
      p_player_id: playerId
    });

  if (error) {
    console.error('Error anonymizing player:', error);
    throw error;
  }

  return data;
};

/**
 * Get admin actions log
 */
export const getAdminActionsLog = async (limit: number = 50): Promise<AdminAction[]> => {
  const { data, error } = await supabase
    .rpc('get_admin_actions_log', { p_limit: limit });

  if (error) {
    console.error('Error getting admin actions log:', error);
    throw error;
  }

  return data || [];
};

/**
 * Search players for anonymization (excludes already deleted/anonymous)
 */
export const searchPlayersForAnonymization = async (searchTerm: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('players')
    .select('id, name, "preName", email, created_at')
    .is('deleted_at', null)
    .eq('is_anonymous', false)
    .or(`name.ilike.%${searchTerm}%,preName.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) {
    console.error('Error searching players:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get all soft-deleted players for cleanup
 */
export const getAllSoftDeletedPlayers = async (): Promise<any[]> => {
  const { data, error } = await supabase
    .from('players')
    .select('id, name, "preName", email, deleted_at, is_guest, created_at')
    .not('deleted_at', 'is', null)  // Only soft-deleted players
    .eq('is_anonymous', false)  // Exclude already anonymized players
    .order('deleted_at', { ascending: false });

  if (error) {
    console.error('Error getting soft-deleted players:', error);
    throw error;
  }

  return data || [];
};

/**
 * Search soft-deleted players for cleanup (filter existing list)
 */
export const searchSoftDeletedPlayers = async (searchTerm: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('players')
    .select('id, name, "preName", email, deleted_at, is_guest, created_at')
    .not('deleted_at', 'is', null)  // Only soft-deleted players
    .eq('is_anonymous', false)  // Exclude already anonymized players
    .or(`name.ilike.%${searchTerm}%,preName.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
    .order('deleted_at', { ascending: false });

  if (error) {
    console.error('Error searching soft-deleted players:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get preview of what will happen when cleaning up a soft-deleted player
 */
export const getCleanupPreview = async (playerId: number): Promise<any> => {
  const { data, error } = await supabase
    .rpc('get_cleanup_preview', { p_player_id: playerId });

  if (error) {
    console.error('Error getting cleanup preview:', error);
    throw error;
  }

  return data;
};

/**
 * Permanently delete a soft-deleted player (anonymize games, then hard delete)
 */
export const cleanupSoftDeletedPlayer = async (playerId: number): Promise<any> => {
  const { data, error } = await supabase
    .rpc('cleanup_soft_deleted_player', { 
      p_player_id: playerId
    });

  if (error) {
    console.error('Error cleaning up soft-deleted player:', error);
    throw error;
  }

  return data;
};
